import { createSlice } from "@reduxjs/toolkit";
import { resolveApiMessage } from "../../helpers/apiMessage";

import {
    getMenus,
    getMenuCategories,
    createMenuCategory,
    updateMenuCategory,
    reorderMenuCategories,
    deleteMenuCategory,
    updateMenu,
    toggleMenuAvailability,
    deleteMenu,
} from "./thunk";

export interface MenuListState {
    categories: any[];
    menus: any[];
    loading: boolean;
    error: string | null;
    raw: any | null;
}

export const initialState: MenuListState = {
    categories: [],
    menus: [],
    loading: false,
    error: null,
    raw: null,
};

const extractPayloadData = (payload: any) => {
    if (!payload) {
        return {};
    }
    if (typeof payload === "object" && "data" in payload) {
        return (payload as Record<string, any>).data ?? {};
    }
    return payload;
};

const extractResponsePayload = (payload: any) => {
    if (payload && typeof payload === "object" && "response" in payload) {
        return extractPayloadData((payload as Record<string, any>).response);
    }

    return extractPayloadData(payload);
};

const getCategoryIdValue = (category: any): string | number | undefined => {
    if (!category || typeof category !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "uuid", "_id", "categoryId", "slug"];
    for (const key of candidateKeys) {
        const value = (category as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const upsertCategory = (categories: any[], category: any) => {
    const identifier = getCategoryIdValue(category);
    if (identifier === undefined) {
        categories.push(category);
        return;
    }

    const index = categories.findIndex((item) => getCategoryIdValue(item) === identifier);
    if (index >= 0) {
        categories[index] = {
            ...categories[index],
            ...category,
        };
        return;
    }

    categories.push(category);
};

const removeCategoryById = (categories: any[], categoryId: string | number) =>
    categories.filter((category) => getCategoryIdValue(category) !== categoryId);

const parseCategories = (payload: any): any[] => {
    const data = extractResponsePayload(payload);

    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray((data as any)?.categories)) {
        return (data as any).categories;
    }

    if (Array.isArray((data as any)?.data)) {
        return (data as any).data;
    }

    if (Array.isArray((data as any)?.data?.data)) {
        return (data as any).data.data;
    }

    if (Array.isArray((data as any)?.items)) {
        return (data as any).items;
    }

    return [];
};

const getPayloadCategoryId = (payload: any): string | number | undefined => {
    if (payload && typeof payload === "object" && "categoryId" in payload) {
        return (payload as Record<string, any>).categoryId as string | number;
    }

    return undefined;
};

const toComparableId = (value: string | number | undefined | null): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

const getMenuIdValue = (menu: any): string | number | undefined => {
    if (!menu || typeof menu !== "object") {
        return undefined;
    }

    const candidateKeys = [
        "id",
        "menuId",
        "menu_id",
        "uuid",
        "slug",
        "code",
        "sku",
    ];

    for (const key of candidateKeys) {
        const value = (menu as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const upsertMenuItem = (menus: any[], menu: any, fallbackId?: string | number) => {
    if (!Array.isArray(menus)) {
        return;
    }

    const identifier = getMenuIdValue(menu) ?? fallbackId;
    const comparableIdentifier = toComparableId(identifier ?? null);

    if (!comparableIdentifier) {
        menus.push(menu);
        return;
    }

    const index = menus.findIndex(
        (item) => toComparableId(getMenuIdValue(item) ?? null) === comparableIdentifier
    );

    if (index >= 0) {
        menus[index] = {
            ...menus[index],
            ...menu,
        };
        return;
    }

    menus.push(
        getMenuIdValue(menu) === undefined
            ? {
                  ...menu,
                  id: identifier,
              }
            : menu
    );
};

const getMenuCategoryIds = (menu: any): string[] => {
    if (!menu || typeof menu !== "object") {
        return [];
    }

    if (Array.isArray((menu as any).categories)) {
        const ids = (menu as any).categories
            .map((category: any) => getCategoryIdValue(category))
            .filter(
                (value: string | number | undefined): value is string | number =>
                    value !== undefined
            )
            .map((value: string | number) => toComparableId(value) ?? "")
            .filter((value: string) => value.length > 0);
        if (ids.length > 0) {
            return Array.from(new Set(ids));
        }
    }

    const directKeys = [
        (menu as any).categoryId,
        (menu as any).category_id,
        (menu as any).categoryID,
        (menu as any).categorySlug,
    ];

    for (const value of directKeys) {
        const comparable = toComparableId(value ?? null);
        if (comparable) {
            return [comparable];
        }
    }

    if (typeof (menu as any).category === "string") {
        const comparable = toComparableId((menu as any).category);
        return comparable ? [comparable] : [];
    }

    if ((menu as any).category && typeof (menu as any).category === "object") {
        const comparable = toComparableId(getCategoryIdValue((menu as any).category));
        return comparable ? [comparable] : [];
    }

    return [];
};

const syncMenuInCategories = (categories: any[], menu: any, fallbackId?: string | number) => {
    if (!Array.isArray(categories)) {
        return;
    }

    const identifier = getMenuIdValue(menu) ?? fallbackId;
    const comparableIdentifier = toComparableId(identifier ?? null);

    if (!comparableIdentifier) {
        return;
    }

    const menuCategoryIds = getMenuCategoryIds(menu);

    categories.forEach((category) => {
        if (!category || typeof category !== "object" || !Array.isArray((category as any).menus)) {
            return;
        }

        const menuList = (category as any).menus as any[];
        const categoryId = toComparableId(getCategoryIdValue(category));
        const index = menuList.findIndex(
            (item) => toComparableId(getMenuIdValue(item) ?? null) === comparableIdentifier
        );

        const shouldContain =
            menuCategoryIds.length === 0 || (categoryId && menuCategoryIds.includes(categoryId));

        if (shouldContain && index >= 0) {
            menuList[index] = {
                ...menuList[index],
                ...menu,
            };
            return;
        }

        if (shouldContain && index === -1) {
            menuList.push(menu);
            return;
        }

        if (!shouldContain && index >= 0) {
            menuList.splice(index, 1);
        }
    });
};

const removeMenuFromCategories = (categories: any[], menuIds: Array<string | number>) => {
    if (!Array.isArray(categories) || menuIds.length === 0) {
        return;
    }

    const comparableIds = menuIds
        .map((menuId) => toComparableId(menuId))
        .filter((value): value is string => Boolean(value));

    if (comparableIds.length === 0) {
        return;
    }

    categories.forEach((category) => {
        if (!category || typeof category !== "object" || !Array.isArray((category as any).menus)) {
            return;
        }

        (category as any).menus = (category as any).menus.filter((menu: any) => {
            const identifier = toComparableId(getMenuIdValue(menu) ?? null);
            return !identifier || !comparableIds.includes(identifier);
        });
    });
};

const getPayloadMenuId = (payload: any): string | number | undefined => {
    if (payload && typeof payload === "object" && "menuId" in payload) {
        return (payload as Record<string, any>).menuId as string | number;
    }

    return undefined;
};

const getPayloadMenuRequest = (payload: any): Record<string, any> | undefined => {
    if (payload && typeof payload === "object" && "request" in payload) {
        const request = (payload as Record<string, any>).request;
        if (request && typeof request === "object") {
            return request as Record<string, any>;
        }
    }

    return undefined;
};

const parseMenusFromPayload = (payload: any): any[] => {
    const data = extractResponsePayload(payload);

    if (Array.isArray(data)) {
        return data;
    }

    if (data && typeof data === "object") {
        if (Array.isArray((data as any).menus)) {
            return (data as any).menus;
        }

        if (Array.isArray((data as any).items)) {
            return (data as any).items;
        }

        if (Array.isArray((data as any).data)) {
            return (data as any).data;
        }

        if ((data as any).menu && typeof (data as any).menu === "object") {
            return [(data as any).menu];
        }
    }

    if (data && typeof data === "object") {
        return [data];
    }

    return [];
};

const composeMenuUpdate = (
    menu: any,
    fallbackRequest?: Record<string, any>,
    fallbackId?: string | number
) => {
    let mergedMenu = menu && typeof menu === "object" ? { ...menu } : {};

    if (fallbackRequest && typeof fallbackRequest === "object") {
        mergedMenu = {
            ...fallbackRequest,
            ...mergedMenu,
        };
    }

    const identifier = getMenuIdValue(mergedMenu) ?? fallbackId;

    if (identifier !== undefined && getMenuIdValue(mergedMenu) === undefined) {
        mergedMenu = {
            ...mergedMenu,
            id: identifier,
        };
    }

    return mergedMenu;
};

const applyMenuUpdateFromPayload = (state: MenuListState, payload: any) => {
    const fallbackId = getPayloadMenuId(payload);
    const fallbackRequest = getPayloadMenuRequest(payload);
    const menus = parseMenusFromPayload(payload);

    if (menus.length === 0) {
        if (fallbackRequest) {
            const menu = composeMenuUpdate({}, fallbackRequest, fallbackId);
            upsertMenuItem(state.menus, menu, fallbackId);
            syncMenuInCategories(state.categories, menu, fallbackId);
        }
        return;
    }

    menus.forEach((menu) => {
        const identifier = getMenuIdValue(menu) ?? fallbackId;
        const mergedMenu = composeMenuUpdate(menu, fallbackRequest, identifier);
        upsertMenuItem(state.menus, mergedMenu, identifier);
        syncMenuInCategories(state.categories, mergedMenu, identifier);
    });
};

const applyMenuDeleteFromPayload = (state: MenuListState, payload: any) => {
    const menus = parseMenusFromPayload(payload);
    const fallbackId = getPayloadMenuId(payload);

    const idsToRemove = menus
        .map((menu) => getMenuIdValue(menu))
        .filter((value): value is string | number => value !== undefined);

    if (fallbackId !== undefined && !idsToRemove.includes(fallbackId)) {
        idsToRemove.push(fallbackId);
    }

    if (idsToRemove.length === 0) {
        return;
    }

    const comparableIds = idsToRemove
        .map((menuId) => toComparableId(menuId))
        .filter((value): value is string => Boolean(value));

    state.menus = state.menus.filter((menu) => {
        const identifier = toComparableId(getMenuIdValue(menu) ?? null);
        return !identifier || !comparableIds.includes(identifier);
    });

    removeMenuFromCategories(state.categories, idsToRemove);
};

const MenuListSlice = createSlice({
    name: "MenuList",
    initialState,
    reducers: {
        clearMenuList: (state) => {
            state.categories = [];
            state.menus = [];
            state.loading = false;
            state.error = null;
            state.raw = null;
        },
        clearMenuError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getMenus.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getMenus.fulfilled, (state, action) => {
            const data = extractPayloadData(action.payload);
            const categories = parseCategories(action.payload);
            const menusFromResponse = parseMenusFromPayload(action.payload);
            const menusFromCategories = categories.reduce((result: any[], category: any) => {
                if (Array.isArray(category?.menus)) {
                    return result.concat(category.menus);
                }
                return result;
            }, [] as any[]);

            state.categories = categories;
            state.menus = menusFromResponse.length > 0 ? menusFromResponse : menusFromCategories;
            state.raw = data;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getMenus.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(getMenuCategories.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getMenuCategories.fulfilled, (state, action) => {
            state.categories = parseCategories(action.payload);
            state.raw = extractResponsePayload(action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getMenuCategories.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(createMenuCategory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createMenuCategory.fulfilled, (state, action) => {
            const data = extractResponsePayload(action.payload);

            if (Array.isArray(data)) {
                data.forEach((category) => upsertCategory(state.categories, category));
            } else if (data && typeof data === "object") {
                upsertCategory(state.categories, data);
            }

            state.loading = false;
            state.error = null;
        });
        builder.addCase(createMenuCategory.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(updateMenuCategory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateMenuCategory.fulfilled, (state, action) => {
            const data = extractResponsePayload(action.payload);
            const fallbackId = getPayloadCategoryId(action.payload);

            if (Array.isArray(data)) {
                data.forEach((category) => upsertCategory(state.categories, category));
            } else if (data && typeof data === "object") {
                if (fallbackId !== undefined && getCategoryIdValue(data) === undefined) {
                    upsertCategory(state.categories, {
                        ...data,
                        id: fallbackId,
                    });
                } else {
                    upsertCategory(state.categories, data);
                }
            } else if (fallbackId !== undefined) {
                const index = state.categories.findIndex((category) => getCategoryIdValue(category) === fallbackId);
                if (index >= 0) {
                    state.categories[index] = {
                        ...state.categories[index],
                    };
                }
            }

            state.loading = false;
            state.error = null;
        });
        builder.addCase(updateMenuCategory.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(reorderMenuCategories.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(reorderMenuCategories.fulfilled, (state, action) => {
            state.categories = parseCategories(action.payload);
            state.raw = extractResponsePayload(action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(reorderMenuCategories.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(deleteMenuCategory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteMenuCategory.fulfilled, (state, action) => {
            const data = extractResponsePayload(action.payload);
            const fallbackId = getPayloadCategoryId(action.payload);

            if (Array.isArray(data) && data.length > 0) {
                const idsToRemove = data
                    .map((category) => getCategoryIdValue(category))
                    .filter((value): value is string | number => value !== undefined);

                if (idsToRemove.length > 0) {
                    state.categories = state.categories.filter((category) => {
                        const identifier = getCategoryIdValue(category);
                        return identifier === undefined || !idsToRemove.includes(identifier);
                    });
                }
            } else if (fallbackId !== undefined) {
                state.categories = removeCategoryById(state.categories, fallbackId);
            }

            state.loading = false;
            state.error = null;
        });
        builder.addCase(deleteMenuCategory.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(updateMenu.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateMenu.fulfilled, (state, action) => {
            applyMenuUpdateFromPayload(state, action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(updateMenu.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(toggleMenuAvailability.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(toggleMenuAvailability.fulfilled, (state, action) => {
            applyMenuUpdateFromPayload(state, action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(toggleMenuAvailability.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
        builder.addCase(deleteMenu.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteMenu.fulfilled, (state, action) => {
            applyMenuDeleteFromPayload(state, action.payload);
            state.loading = false;
            state.error = null;
        });
        builder.addCase(deleteMenu.rejected, (state, action) => {
            state.loading = false;
            state.error = resolveApiMessage((action as any).payload ?? action.error);
        });
    },
});

export const { clearMenuList, clearMenuError } = MenuListSlice.actions;
export default MenuListSlice.reducer;
